import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Grid, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'throttle-debounce';
import strings from '../../Globalization/index';
import ModelFormProvider from '../../contexts/ModelForm';
import util from '../../util';
import ActionIconButton from '../common/ActionIconButton';
import { FieldMappers } from '../form';
import { dataStore } from '../../Store';


const HeaderFilter = ({ formik, column, fieldMappers, combos }) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const field = column.headerFilterField || column.formField || column.field;
	const fieldLabel = column?.headerFilterProps?.label || column.label || column.header || field;
	let fieldType = column.type;
	if (!column.type) {
		fieldType = column.lookup ? 'autocomplete' : 'string';
	}
	if (column.lookup) {
		fieldType = 'autocomplete';
	}
	const inputProps = useMemo(
		() => ({
			column: {
				...column,
				...column.headerFilterProps,
				label: strings[fieldLabel] || fieldLabel,
				inputSiblingComponents: column.headerFilterSiblingComponents,
				fullWidth: false,
				required: false,
				readOnly: false
			},
			field,
			fieldLabel: strings[fieldLabel] || fieldLabel,
			otherProps: {},
			inlineLabel: false,
			activeRecord: { combos }
		}),
		[fieldType, field, fieldLabel, combos, column]
	);
	const gridItemProps = useMemo(() => {
		let val = {
			...(column.headerFilterWrapperProps),
			sx: {
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				...column?.headerFilterWrapperProps?.sx
			}
		};
		if ('filterColSize' in column) {
			val = {
				...val,
				size: {
					xs: 12,
					md: column.filterColSize
				}
			};
		} else {
			val.sx.flex = 1;
		}
		return val;
	}, []);

	const InputComponent = fieldMappers[fieldType];


	return (
		<Grid size={12} {...gridItemProps}>
			{!!InputComponent && <InputComponent {...inputProps} formik={formik} navigate={navigate} />}
		</Grid>
	);
};
//Indipendent standaloneFilterColumns Form component to accomodate formik and logic
const HeaderFilterModelForm = ({ standaloneFilterColumns, filterModel, standaloneFilters, onSubmit, modelConfig, combos, activeRecord, ...rest }) => {
	const fieldMappersRef = useRef(new FieldMappers());
	const theme = useTheme();
	const [status, setStatus] = useState('pristine');
	
	const handleOnSubmit = async (values) => {
		setStatus('submitting');
		const getOperatorValue = ({ key, value }) => {
			if (Array.isArray(value)) {
				return 'isAnyOf';
			} else if (typeof value === 'string') {
				return 'contains';
			}
			return 'is';
		};
		const filterModelItems = [];
		const newStandaloneFilters = {};
		let index = 1;
		for (let [key, value] of Object.entries(values)) {
			if (value) {
				const headerFiltersIndex = standaloneFilterColumns.findIndex(
					(item) => item.headerFilterField === key || item.formField === key || item.field === key
				);
				if (standaloneFilterColumns[headerFiltersIndex]?.standaloneHeaderFilter) {
					const submitFormat = standaloneFilterColumns[headerFiltersIndex]?.submitFormat;
					if (submitFormat) {
						value = dayjs(value).format(submitFormat);
					}
					if (['date', 'datetime'].includes(standaloneFilterColumns[headerFiltersIndex]?.type?.toLowerCase()) &&
						dayjs(value).isValid() &&
						(!!standaloneFilterColumns[headerFiltersIndex]?.format ||
							!!standaloneFilterColumns[headerFiltersIndex]?.formFormat && !submitFormat)
					) {
						value = dayjs(value).format(
							standaloneFilterColumns[headerFiltersIndex]?.format ||
							standaloneFilterColumns[headerFiltersIndex]?.formFormat
						);
					}
					newStandaloneFilters[key] = value;
				} else {
					filterModelItems.push({
						id: index,
						columnField: key,
						operatorValue: getOperatorValue({ key, value }),
						value: value
					});
				}
				index++;
			}
		}
		const newFilterModel = {
			items: filterModelItems,
			linkOperator: 'and'
		};
		if (typeof onSubmit === 'function') {
			await onSubmit({ filterModel: newFilterModel, standaloneFilters: newStandaloneFilters });
		}
		setStatus('submitted');
	};

	const handleOnChange = debounce(500, ({ formik: formikBag, value, field }) => {
		if (modelConfig.headerFilterApplyTrigger === 'onChange') {
			handleOnSubmit({ ...formikBag.values, [field]: value }, formikBag);
		}
	});
	const initialValues = useMemo(() => {
		let initialVals = {};
		for (const headerFilter of standaloneFilterColumns) {
			initialVals[headerFilter.field] = modelConfig?.columns?.find?.(
				(col) => col.field === headerFilter.field
			)?.defaultValue;
		}

		return { ...initialVals, ...standaloneFilters };
	}, [standaloneFilterColumns, standaloneFilters]);

	const formik = useFormik({
		initialValues: initialValues,
		enableReinitialize: true,
		onSubmit: handleOnSubmit
	});

	const filters = useMemo(
		() =>
			standaloneFilterColumns?.map((column) => {
				column.onChange = handleOnChange;
				return column;
			}).filter(columns => !columns.hideStandalonHeaderFilters),
		[standaloneFilterColumns]
	);
	const handleOnClearBtnClick = useCallback(
		(e) => {
			if (typeof modelConfig.onClearHeaderFilter === 'function') {
				return modelConfig.onClearHeaderFilter({ initialValues: modelConfig.initialValues, formik });
			}
			if (modelConfig.title) {
				const startDate = modelConfig.initialValues.startDate;
				const endDate = modelConfig.initialValues.endDate
				formik.resetForm({ values: { startDate, endDate } });
			} else {
				formik.resetForm({ values: {} });
			}
			formik.handleSubmit();
		},
		[status]
	);
	const getLicenseType = () => {
		const { auth = {} } = dataStore.getState();
		const { tags = {} } = auth;
		return tags?.LicenseAppName.includes('catviewer')
	}
	const getFormik = useCallback(() => formik, [formik]);
	const getActiveRecord = useCallback(() => ({ combos }), [combos]);
	const getLicense = getLicenseType();
	return (
		<ModelFormProvider value={{ formik, activeRecord: { combos }, modelConfig, getActiveRecord, getFormik }}>
			<Stack
				spacing={3}
				direction={'row'}
				{...rest}
				component={'form'}
				onSubmit={formik.handleSubmit}
				{...modelConfig?.headerFilterProps}
			>
				<Box sx={{ flexGrow: 1, flex: 1, display: 'flex' }}>
					<Grid
						container
						spacing={1}
						className="headerFilterInputs"
						flex={1}
					>
						{filters?.map(
							({ headerFilterComponent: HeaderFilterComponent = HeaderFilter, ...column }, index) => (
								<HeaderFilterComponent
									column={{ ...column, onChange: handleOnChange }}
									formik={formik}
									fieldMappers={fieldMappersRef.current}
									combos={combos}
									key={`headerFilter-${index}`}
								/>
							)
						)}
					</Grid>
				</Box>
				{(!modelConfig?.headerFilterApplyTrigger ||
					modelConfig?.headerFilterApplyTrigger === 'onSubmit') && (
						<Stack
							className="headerFilterActions"
							direction="row"
							sx={{ gap: theme.spacing() }}
							alignItems="center"
							justifyContent="flex-end"
						>
							<ActionIconButton variant="contained" type="submit" color="success">
								<SearchIcon fontSize="small" />
							</ActionIconButton>

							<ActionIconButton
								variant="contained"
								type="button"
								color="error"
								onClick={handleOnClearBtnClick}
							>
								<CloseIcon fontSize="small" />
							</ActionIconButton>
							{getLicense && Array.isArray(modelConfig.headerFilterProps?.actions) &&
								modelConfig.headerFilterProps?.actions?.map(
									({ children, text, Component = Button, ...action }, i) => (
										<Component {...action} key={`headerFilterProp-action-${i}`}>
											{text || children}
										</Component>
									)
								)}
						</Stack>
					)}
			</Stack>
		</ModelFormProvider>
	);
}

export default memo(HeaderFilterModelForm, (props, nextProps) =>
	util.deepEqual(
		[props.combos, props.standaloneFilters, props.standaloneFilterColumns],
		[nextProps.combos, nextProps.standaloneFilters, nextProps.standaloneFilterColumns]
	)
);
